<template>
  <div>
    <highchart-wrapper
      v-if="hasData"
      :use-exporting="false"
      :payload="chartPayload"
      :is-currency="true"
      :container-id="containerId"
    ></highchart-wrapper>
    <p v-else class="text-muted text-sm m-t-md">
      No category breakdown data available for this payout.
    </p>
  </div>
</template>
<script>
import HighchartWrapper from './reports/highchart_wrapper.vue';

export default {
  name: 'payout-category-chart',
  props: {
    categories: {
      type: Array,
      required: true,
    },
    containerId: {
      type: String,
      default: 'payout-category-chart',
    },
    title: {
      type: String,
      default: 'Net by category',
    },
    useCategoryColors: {
      // The color map below is keyed by payment category names; turn this off
      // when the bars are something else (e.g. coaches) to use the theme palette
      type: Boolean,
      default: true,
    },
  },
  components: { HighchartWrapper },
  computed: {
    hasData() {
      return this.categories && this.categories.length > 0;
    },
    colors() {
      return {
        'Add-ons': '#8562C6',
        'Recurring subscription': '#4299D7',
        'Camp / clinic': '#663DAE',
        Trial: '#F7D06E',
        'Full season': '#27AA83',
        'Drop in': '#D64343',
        'Private lessons': '#102A42',
        'Private Lesson': '#102A42',
        Fundraisers: '#C5F7E1',
        Fundraiser: '#C5F7E1',
        Donations: '#66D6AD',
        Products: '#54D1DB',
        'One-off Invoice': '#F9703E',
        Registration: '#4299D7',
        Other: '#F1F5F8',
      };
    },
    sortedCategories() {
      return [...this.categories].sort((a, b) => b.amount - a.amount);
    },
    hasDrilldown() {
      return this.sortedCategories.some((cat) => cat.subcategories && cat.subcategories.length > 0);
    },
    drilldownSeries() {
      const series = [];

      this.sortedCategories.forEach((cat) => {
        if (cat.subcategories && cat.subcategories.length > 0) {
          // First level drilldown (subcategories)
          series.push({
            name: cat.name,
            id: cat.name,
            type: 'bar',
            pointWidth: 20,
            groupPadding: 0.05,
            dataLabels: {
              enabled: true,
              format: '${point.y:,.0f}',
              align: 'left',
              verticalAlign: 'middle',
              inside: false,
              crop: false,
              overflow: 'allow',
              style: {
                fontSize: '13px',
                fontWeight: '600',
                color: '#243A52',
                textOutline: 'none',
              },
            },
            data: cat.subcategories.map((sub) => ({
              name: sub.name || 'Uncategorized',
              y: sub.amount / 100.0,
              drilldown: sub.details && sub.details.length > 0 ? `${cat.name}_${sub.name}` : null,
            })).sort((a, b) => b.y - a.y),
          });

          // Second level drilldown (details)
          cat.subcategories.forEach((sub) => {
            if (sub.details && sub.details.length > 0) {
              series.push({
                name: sub.name,
                id: `${cat.name}_${sub.name}`,
                type: 'bar',
                pointWidth: 20,
                groupPadding: 0.15,
                dataLabels: {
                  enabled: true,
                  format: '${point.y:,.0f}',
                  align: 'left',
                  verticalAlign: 'middle',
                  inside: false,
                  crop: false,
                  overflow: 'allow',
                  style: {
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#243A52',
                    textOutline: 'none',
                  },
                },
                data: sub.details.map((detail) => [
                  detail.name || 'Uncategorized',
                  detail.amount / 100.0,
                ]).sort((a, b) => b[1] - a[1]),
              });
            }
          });
        }
      });

      return series;
    },
    chartPayload() {
      const subtitleText = this.hasDrilldown
        ? 'Click on a bar for detailed breakdown'
        : 'Revenue breakdown for this payout';

      const payload = {
        chart: {
          type: 'bar',
          marginLeft: 180,
          marginRight: 40,
          spacingBottom: 20,
          height: Math.max(200, this.sortedCategories.length * 50 + 80),
        },
        lang: {
          noData: 'No category data available',
        },
        // An empty title suppresses the chart's own header block so the host
        // page can render its own heading with consistent typography
        title: {
          text: this.title ? `<label>${this.title}</label>` : null,
          useHTML: true,
          align: 'left',
        },
        subtitle: {
          text: this.title ? subtitleText : null,
          align: 'left',
        },
        xAxis: {
          type: 'category',
          labels: {
            style: {
              fontSize: '13px',
              fontWeight: '600',
              color: '#243A52',
            },
            x: -10,
            reserveSpace: true,
            align: 'right',
          },
          lineWidth: 0,
        },
        yAxis: {
          title: {
            text: '',
            style: {
              color: '#48647F',
              fontSize: '13px',
            },
          },
          labels: {
            format: '${value:,.0f}',
            style: {
              color: '#48647F',
            },
          },
          gridLineColor: '#DAE2EC',
          gridLineDashStyle: 'Dash',
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          series: {
            borderRadius: 6,
            pointPadding: 0.2,
            groupPadding: 0.1,
            colorByPoint: true,
            dataLabels: {
              enabled: true,
              format: '${point.y:,.0f}',
              align: 'left',
              verticalAlign: 'middle',
              inside: false,
              crop: false,
              overflow: 'allow',
              style: {
                fontSize: '13px',
                fontWeight: '600',
                color: '#243A52',
                textOutline: 'none',
              },
            },
          },
          bar: {
            minPointLength: 3,
            groupPadding: 0.25,
            pointWidth: 20,
          },
        },
        tooltip: {
          headerFormat: '<span style="font-size: 14px; font-weight: 600; color: #243A52">{point.key}</span><br/>',
          pointFormat: '<span style="color: #48647F; font-size: 14px">${point.y:,.2f}</span>',
          useHTML: true,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderWidth: 1,
          borderColor: '#DAE2EC',
          borderRadius: 8,
          shadow: true,
          padding: 12,
        },
        series: [{
          name: 'Revenue',
          colorByPoint: true,
          data: this.sortedCategories.map((cat) => ({
            name: cat.name,
            y: cat.amount / 100.0,
            drilldown: cat.subcategories && cat.subcategories.length > 0 ? cat.name : null,
          })),
        }],
        drilldown: {
          activeAxisLabelStyle: {
            color: '#243A52',
          },
          breadcrumbs: {
            buttonTheme: {
              fill: '#F1F5F8',
              padding: 8,
              stroke: '#324D67',
              'stroke-width': 1,
              style: {
                color: '#324D67',
              },
            },
            floating: true,
            position: {
              align: 'right',
            },
            showFullPath: false,
            zIndex: 5000,
          },
          activeDataLabelStyle: {
            color: '#243A52',
            textOutline: 'none',
          },
          series: this.drilldownSeries,
        },
      };

      if (this.useCategoryColors) {
        payload.colors = this.sortedCategories.map((cat) => this.colors[cat.name] || '#4299D7');
      }

      return payload;
    },
  },
};
</script>
